"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * The orders module provides access to the order data from the EVA back-end
 * @preferred
 */
var base_1 = require("./base");
var service_1 = require("../service");
var payments_1 = require("./payments");
var _ = require("lodash");
/**
 * Provides access to an order available in EVA
 */
var Order = /** @class */ (function (_super) {
    __extends(Order, _super);
    /**
     * This class wraps an order from EVA and the operations you can perform on it.
     * You should supply the order identifier when creating an instance of this class
     *
     * @param  {number | string}    id      The identifier of the order to retrieve
     * @return {void}                       This method doesn't return anything
     */
    function Order(id) {
        var _this = _super.call(this, id) || this;
        /**
         * Flag to include pending payments in fetch
         *
         * @type {boolean}
         * @memberof Order
         */
        _this.includePendingPayments = false;
        /**
         * Flag to include failed payments in fetch
         *
         * @type {boolean}
         * @memberof Order
         */
        _this.includeFailedPayments = false;
        _this.payment = new payments_1.Payments(_this);
        return _this;
    }
    /**
     * Returns if the order state 'completed' applies to the order
     * @return {boolean} Indicates the state is active
     */
    Order.prototype.isCompleted = function () {
        return this._isCompleted === true;
    };
    /**
     * Returns if the order state 'shipped' applies to the order
     * @return {boolean} Indicates the state is active
     */
    Order.prototype.isShipped = function () {
        return this._isShipped === true;
    };
    /**
     * Returns if the order state 'paid' applies to the order
     * @return {boolean} Indicates the state is active
     */
    Order.prototype.isPaid = function () {
        return this._isPaid === true;
    };
    /**
     * Returns if the order state 'invoiced' applies to the order
     * @return {boolean} Indicates the state is active
     */
    Order.prototype.isInvoiced = function () {
        return this._isInvoiced === true;
    };
    /**
     * Returns if the order state 'cancelled' applies to the order
     * @return {boolean} Indicates the state is active
     */
    Order.prototype.isCancelled = function () {
        return this._isCancelled === true;
    };
    /**
     * Returns the current open amount still to be paid for the order
     * @return {number}
     */
    Order.prototype.getOpenAmount = function () {
        return this.openAmount;
    };
    /**
     * Returns the total value of all the items in the order with VAT
     * @return {number} The value of the items in the order with VAT
     */
    Order.prototype.getTotalAmountInTax = function () {
        return this.totalAmountInTax;
    };
    /**
     * Return the current order amounts
     *
     * @returns {EVA.Core.OrderAmounts}
     * @memberof Order
     */
    Order.prototype.getOrderAmounts = function () {
        return this._orderAmounts;
    };
    /**
     * Return order lines by type
     * @return {Array<EVA.Core.OrderLineDto>} Array of order lines
     */
    Order.prototype.getOrderLinesByType = function (orderLineType) {
        return _.filter(this.data.Lines, function (line) {
            return line.Type === orderLineType;
        });
    };
    /**
     * Return the total amount of shipping costs
     * @return {number} The total value of the shipping costs
     */
    Order.prototype.getTotalShippingAmount = function (inTax) {
        if (inTax === void 0) { inTax = true; }
        var targetField;
        if (inTax) {
            targetField = "TotalAmountInTax";
        }
        else {
            targetField = "TotalAmount";
        }
        return _.sumBy(this.getOrderLinesByType(5), targetField);
    };
    /**
     * Return the total amount of ShippingCosts
     * @return {number} The total value of the shipping costs
     */
    Order.prototype.getTotalDiscountAmount = function (inTax) {
        if (inTax === void 0) { inTax = true; }
        var targetField;
        if (inTax) {
            targetField = "TotalAmountInTax";
        }
        else {
            targetField = "TotalAmount";
        }
        return _.sumBy(this.getOrderLinesByType(1), targetField);
    };
    /**
     * Returns the current order requirement validation data
     * @return {EVA.Core.Validation.RequiredData} The order requirement validation data
     */
    Order.prototype.getRequirements = function () {
        return this.requirements;
    };
    /**
     * Checks if there are any invalid requirements in the current order requirement validation data
     * @return {boolean} Indicates if the order requirements are all met or not
     */
    Order.prototype.isValid = function () {
        var customerRequirementsValid = _.find(_.get(this.requirements, "Customer"), { IsValid: false }) === undefined;
        var orderRequirementsValid = _.find(_.get(this.requirements, "Order"), { IsValid: false }) === undefined;
        var orderLineRequirementsValid = _.find(_.get(this.requirements, "OrderLines"), { IsValid: false }) === undefined;
        return customerRequirementsValid && orderRequirementsValid && orderLineRequirementsValid;
    };
    /**
     * Fetches and updates the order requirement validation data
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetRequiredDataForOrder, EVA.Core.Services.GetRequiredDataForOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.validate = function (requiredFor, storeRequirements) {
        var _this = this;
        if (requiredFor === void 0) { requiredFor = 15; }
        if (storeRequirements === void 0) { storeRequirements = true; }
        // Create our service instance
        //
        var service = new service_1.EVAService("GetRequiredDataForOrder");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        // RequiredFor {
        //   PlaceOrder = 1,
        //   Payment = 2,
        //   Ship = 4,
        //   Invoice = 8,
        //   All = 15,
        // }
        //
        var requestData = {
            OrderID: this.getId(),
            RequiredFor: requiredFor
        };
        return new Promise(function (resolve, reject) {
            service.call(requestData)
                .then(function (serviceResult) {
                if (storeRequirements) {
                    _this.requirements = serviceResult.response.RequiredData;
                }
                resolve(serviceResult);
            })
                .catch(reject);
        });
    };
    Object.defineProperty(Order.prototype, "stockOrganizationUnits", {
        /**
         * Returns the list of different organizations where orderline items are te be picked up
         * @return {number} The list of organizations
         */
        get: function () {
            return this._stockOrganizationUnits;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Order.prototype, "requestedDates", {
        /**
         * Returns the list of different organizations where orderline items are te be picked up
         * @return {number} The list of organizations
         */
        get: function () {
            return this._requestedDates;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Fetches the order details
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.fetch = function () {
        // Build the request data
        //
        var requestOrderData = {
            OrderID: this.getId(),
            IncludePendingPayments: this.includePendingPayments,
            IncludeFailedPayments: this.includeFailedPayments,
        };
        return _super.prototype.fetch.call(this, "GetOrder", requestOrderData);
    };
    /**
     * Sets the current item details using the fetched service response.
     * Extending classes should override this method to pull the details from response
     * @param  {EVA.Core.Services.GetOrderResponse}  response    The response from the service call
     * @return {EVA.Core.OrderDto}              The order details
     */
    Order.prototype.setData = function (response) {
        // Collect the data exposed outside of the OrderDto
        //
        this.openAmount = response.OpenAmount || 0;
        this.totalAmountInTax = response.TotalAmountInTax || 0;
        this._isCompleted = response.IsCompleted === true;
        this._isShipped = response.IsShipped === true;
        this._isPaid = response.IsPaid === true;
        this._isInvoiced = response.IsInvoiced === true;
        this._isCancelled = response.IsCancelled === true;
        this._orderAmounts = response.Amounts;
        // Set the order data
        //
        this.data = response.Result || response.Order || response.ShoppingCart || response;
        // Set or update the ID
        //
        this.id = this.data.ID;
        // Collect the stock organizations from the order lines
        // This used to be per line but is now centralised on the order
        //
        this._stockOrganizationUnits = [this.data.OrganizationUnitID];
        // Collect the requested delivery dates from the order lines
        //
        this._requestedDates = _.without(_.uniq(_.map(this.data.Lines, function (line) {
            if (line.LineActionType === 4 && line.RequestedDate) {
                return line.RequestedDate;
            }
        })), undefined);
        return this.data;
    };
    /**
     * Attaches a customer to the order
     * @param  {LoggedInUser}       customer       The current user class that is to be set as the customer for the order
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.setCustomer = function (customer) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("AttachCustomerToOrder");
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                UserID: customer.getId(),
                OrderID: _this.getId()
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Sets the currency for the order
     * @param  {string}       currencyID       The identifier of the currency
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.setCurrency = function (currencyID) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("UpdateOrderCurrency");
            var requestData = {
                OrderID: _this.getId(),
                CurrencyID: currencyID,
                SessionID: null,
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Retrieve the current warehouse data for this order
     *
     * @see https://newblack1.aha.io/features/EVA-17
     */
    Order.prototype.getWarehouseData = function () {
        var service = new service_1.EVAService("GetWarehouseOrderData");
        var requestData = {
            OrderID: this.getId(),
        };
        return service.call(requestData);
    };
    /**
     * Dictates whether this order will be partially delivered or not
     * @param allowPartialPick whether to allow partial pick up or not
     * @see https://newblack1.aha.io/features/EVA-17
     */
    Order.prototype.setWarehouseData = function (allowPartialPick) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("SetWarehouseOrderData");
            var requestData = {
                OrderID: _this.getId(),
                WarehouseOrderData: {
                    AllowPartialPick: allowPartialPick
                },
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.getWarehouseData().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Sets a requested date on an order line
     * @param orderLineID the orderLine we want to modify the requst date of
     * @param requestedDate the request date in question
     */
    Order.prototype.setRequestedDate = function (orderLines, requestedDate) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("SetRequestedDate");
            var requestData = {
                OrderID: _this.getId(),
                OrderLines: orderLines,
                RequestedDate: requestedDate
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Attaches a customer to the order
     * @param  {LoggedInUser}       customer       The current user class that is to be set as the customer for the order
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.setPickupOrganizationUnit = function (organizationUnitID) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("SetPickupOrganizationUnit");
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                OrganizationUnitID: organizationUnitID,
                OrderID: _this.getId(),
                SessionID: undefined,
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (orderResponse) {
                    var finalResponse = __assign({}, orderResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Places the order. It's usually wise to call validate(...) before trying to place an order
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.PlaceOrder, EVA.Core.Services.PlaceOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.placeOrder = function () {
        // Create our service instance
        //
        var service = new service_1.EVAService("PlaceOrder");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            OrderID: this.getId()
        };
        // Perform the call and capture the resulting promise
        //
        var promise = service.call(requestData);
        return promise;
    };
    /**
     * Ships the order. It's usually wise to call validate(...) before trying to ship an order
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.ShipOrder, EVA.Core.Services.ShipOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.shipOrder = function (lines, force) {
        // Create our service instance
        //
        var service = new service_1.EVAService("ShipOrder");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            OrderID: this.getId(),
            Lines: lines,
            Force: force,
        };
        // Perform the call and capture the resulting promise
        //
        var promise = service.call(requestData);
        return promise;
    };
    Order.prototype.produceInvoice = function (options) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("ProduceInvoice");
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                OrderID: _this.data.ID
            };
            // Property mapping between IEVAProduceInvoiceOptions and the service request
            //
            var items = {
                "DownloadInvoice": options.downloadInvoice,
                "EmailAddress": options.emailAddress,
                "PrintInvoice": options.printInvoice,
                "Remark": options.remark,
                "StationID": options.stationId
            };
            var key;
            for (key in items) {
                if (items[key]) {
                    requestData[key] = items[key];
                }
            }
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(resolve, reject);
        });
    };
    Order.prototype.produceDocuments = function (options) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("ProduceDocuments");
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                PrintInvoice: options.printInvoice,
                PrintReceipt: options.printReceipt,
                EmailInvoice: options.emailInvoice,
                EmailAddress: options.emailAddress,
                StationID: options.stationId,
                OrderID: _this.id,
                Remark: options.remark
            };
            // Perform the call and capture the resulting promise
            //
            return service.call(requestData).then(resolve, reject);
        });
    };
    /**
     * Get the download url for this order
     *
     * @return {Promise<string>} The URL to the download of this order
     */
    Order.prototype.getDownloadUrl = function () {
        return this.produceInvoice({ downloadInvoice: true })
            .then(function (serviceResult) {
            return serviceResult.response.Url;
        });
    };
    Order.prototype.getReturnableStatusForOrder = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("GetReturnableStatusForOrder");
            var requestData = {
                OrderID: _this.id
            };
            service.call(requestData)
                .then(function (returnResolve) { resolve(returnResolve); }, reject);
        });
    };
    Order.prototype.getReturnOrdersForOrder = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("GetReturnOrdersForOrder");
            var requestData = {
                OrderID: _this.id,
            };
            service.call(requestData)
                .then(function (returnResolve) { resolve(returnResolve); }, reject);
        });
    };
    Order.prototype.createCustomerReturn = function (lines) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("CreateCustomerReturn");
            var requestData = {
                OrderID: _this.id,
                Lines: lines
            };
            service.call(requestData)
                .then(function (returnResolve) { resolve(returnResolve); }, reject);
        });
    };
    Order.prototype.createEmployeeReturn = function (lines) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("ReturnOrderLines");
            var requestData = {
                OrderID: _this.id,
                Lines: lines,
                ForceCreate: true // was true in the old sales app, therefore is true here too
            };
            service.call(requestData)
                .then(function (returnResolve) { resolve(returnResolve); }, reject);
        });
    };
    /**
     * For B2B orders
     *
     * Fetches the order view details
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetConfigurableOrderView, EVA.Core.Services.GetConfigurableOrderViewResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.fetchOrderView = function (logicalLevel, groupByProperty) {
        // Build the request data
        //
        var service = new service_1.EVAService("GetConfigurableOrderView");
        var requestData = {
            OrderID: this.getId(),
            LogicalLevel: logicalLevel,
            GroupByPropertyType: groupByProperty
        };
        return service.call(requestData);
    };
    /**
     * Set an order reference for a customer
     *
     * @param customerReference The reference value to set
     * @param customerOrderId The customer order id
     * @see https://newblack1.aha.io/features/EVA-15
     */
    Order.prototype.setCustomerOrderReference = function (customerReference, customerOrderId) {
        // Build the request data
        //
        var service = new service_1.EVAService("SetCustomerReferencesOnOrder");
        var requestData = {
            OrderID: this.getId(),
            CustomerOrderID: customerOrderId,
            CustomerReference: customerReference
        };
        return service.call(requestData);
    };
    /**
     * Set the shopping basket to an existing order. Removes any existing items in the shopping cart
     * @param  {number|Order}       order       Either an order id or an order class to set as the shopping cart
     * @return {Promise}                        A promise resolving when the call to the back-end has completed
     */
    Order.prototype.attach = function (order) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("AttachOrderToSession");
            var orderId;
            if (order instanceof Order) {
                orderId = order.getId();
            }
            else {
                orderId = order;
            }
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                OrderID: orderId,
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.id = orderId;
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Clears the entire shopping basket
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the call to the back-end has completed
     */
    Order.prototype.detach = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("DetachOrderFromSession");
            // Perform the call and capture the resulting promise
            //
            service.call()
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Suspends the current order
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.SuspendOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the call to the back-end has completed
     */
    Order.prototype.suspend = function (description, printReceipt, stationId) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("SuspendOrder");
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                OrderID: _this.getId(),
                Description: description,
                PrintReceipt: printReceipt,
            };
            if (stationId) {
                requestData.StationID = stationId;
            }
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                resolve();
            })
                .catch(reject);
        });
    };
    /**
     * Set the order reference
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.ListOrderLines, EVA.Core.Services.ListOrderLinesResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.listOrderLines = function (orderLineIds, start, limit, shippable, invoiceable, onlyShippable, productTypes) {
        if (start === void 0) { start = 0; }
        if (limit === void 0) { limit = 20; }
        if (shippable === void 0) { shippable = true; }
        if (invoiceable === void 0) { invoiceable = true; }
        if (onlyShippable === void 0) { onlyShippable = false; }
        if (productTypes === void 0) { productTypes = null; }
        var service = new service_1.EVAService("ListOrderLines");
        // Build the request data
        //
        var requestData = {
            OrderID: this.getId()
        };
        if (!_.isEmpty(orderLineIds)) {
            requestData.PageConfig = {
                Start: 0,
                Limit: orderLineIds.length || limit,
                SortProperty: "ID",
                SortDirection: 0,
                Filter: {
                    IDs: _.map(orderLineIds, function (id) { return id; }),
                    Invoiceable: invoiceable,
                    Shippable: shippable,
                    OnlyShippableLines: onlyShippable,
                    ProductTypes: productTypes,
                    Query: null
                }
            };
        }
        return service.call(requestData);
    };
    /**
     * Update customer address data for this order
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.ListOrderLines, EVA.Core.Services.ListOrderLinesResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.copyAddressesFromCustomer = function () {
        var service = new service_1.EVAService("UpdateOrderAddresses");
        // Build the request data
        //
        var requestData = {
            OrderID: this.getId(),
            CopyBillingAddressFromCustomer: true,
            CopyShippingAddressFromCustomer: true
        };
        return service.call(requestData);
    };
    /**
     * Provides access to a list of shipments for this order
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.ListOrderShipments, EVA.Core.Services.ListOrderShipmentsResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.listShipments = function () {
        var service = new service_1.EVAService("ListOrderShipments");
        // Build the request data
        //
        var requestData = {
            OrderID: this.getId()
        };
        return service.call(requestData);
    };
    /**
     * Provides access to a list of blobs for this order
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.ListBlobsForOrder, EVA.Core.Services.ListBlobsForOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.listBlobs = function () {
        var service = new service_1.EVAService("ListBlobsForOrder");
        // Build the request data
        //
        var requestData = {
            OrderID: this.getId()
        };
        return service.call(requestData);
    };
    /**
     * Set the billing address of the order using an AddressBook item
     *
     * @param {number} addressBookId The ID of the AddressBook item
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.setBillingAddressFromAddressBook = function (addressBookId) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("UpdateOrderBillingAddress");
            // Build the request data
            //
            var requestData = {
                OrderID: _this.getId(),
                AddressBookID: addressBookId
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Set the billing address of the order using an raw address
     *
     * @param {number} address The address to set as billing address on the order
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.setBillingAddress = function (address) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("UpdateOrderBillingAddress");
            // Build the request data
            //
            var requestData = {
                OrderID: _this.getId(),
                Address: address
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Set the shipping address of the order using an AddressBook item
     *
     * @param {number} addressBookId The ID of the AddressBook item
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the call to the back-end has completed
     */
    Order.prototype.setShippingAddressFromAddressBook = function (addressBookId) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("UpdateOrderShippingAddress");
            // Build the request data
            //
            var requestData = {
                OrderID: _this.getId(),
                AddressBookID: addressBookId
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Set the shipping address of the order using an raw address
     *
     * @param {number} address The address to set as shipping address on the order
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Order.prototype.setShippingAddress = function (address) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("UpdateOrderShippingAddress");
            // Build the request data
            //
            var requestData = {
                OrderID: _this.getId(),
                Address: address
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Sets the remarks for an order
     *
     * @param {string} remarks The remarks content
     * @returns {Promise<IEVAServiceResponse<EVA.Core.Services.SetCustomOrderData, EVA.API.EmptyResponseMessage>>}
     * @memberof Order
     */
    Order.prototype.setRemark = function (remarks) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("SetRemarkOnOrder");
            // Build the request data
            //
            var requestData = {
                OrderID: _this.getId(),
                Remark: remarks
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Retrieves available discount product options for an order line
     *
     * @param {number} orderLineId
     * @returns {Promise<IEVAServiceResponse<EVA.Core.Services.GetPickProductDiscountOptionsForOrderLine, EVA.Core.Services.GetPickProductDiscountOptionsForOrderLineResponse>>}
     * @memberof Order
     */
    Order.prototype.getDiscountProductOptionsForOrderline = function (orderLineId) {
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("GetPickProductDiscountOptionsForOrderLine");
            // Build the request data
            //
            var requestData = {
                OrderLineID: orderLineId
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(resolve)
                .catch(reject);
        });
    };
    /**
     * Retrieves available discount product options for an order
     *
     * @param {number} orderId
     * @returns {Promise<IEVAServiceResponse<EVA.Core.Services.GetPickProductDiscountOptionsForOrder, EVA.Core.Services.GetPickProductDiscountOptionsForOrderResponse>>}
     * @memberof Order
     */
    Order.prototype.getDiscountProductOptionsForOrder = function (orderId) {
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("GetPickProductDiscountOptionsForOrder");
            // Build the request data
            //
            var requestData = {
                OrderID: orderId
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(resolve)
                .catch(reject);
        });
    };
    /**
     * Sets the chosen discount product for an order line
     *
     * @param {number} orderLineId
     * @param {(number | null)} selectionId
     * @returns {Promise<IEVAServiceResponse<EVA.Core.Services.SetPickProductDiscountOptionsForOrderLine, EVA.API.EmptyResponseMessage>>}
     * @memberof Order
     */
    Order.prototype.setDiscountProductForOrderline = function (orderLineId, selectionId) {
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("SetPickProductDiscountOptionsForOrderLine");
            // Build the request data
            //
            var requestData = {
                OrderLineID: orderLineId,
                Selection: selectionId,
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(resolve)
                .catch(reject);
        });
    };
    return Order;
}(base_1.FetchableItem));
exports.Order = Order;
/**
 * Provides access to a list of customer orders available in EVA
 */
var CustomerOrderList = /** @class */ (function (_super) {
    __extends(CustomerOrderList, _super);
    function CustomerOrderList(id, limit, start, sortDirection, sortProperty) {
        if (limit === void 0) { limit = 15; }
        if (start === void 0) { start = 0; }
        if (sortDirection === void 0) { sortDirection = 0; }
        if (sortProperty === void 0) { sortProperty = "product_id"; }
        var _this = _super.call(this, limit, start, sortDirection, sortProperty) || this;
        _this.id = id;
        return _this;
    }
    /**
     * Sets the current item details using the fetched service response.
     * Extending classes should override this method to pull the details from response
     * @param  {RES} response The response from the service call
     * @return {T}            The item details
     */
    CustomerOrderList.prototype.setData = function (response) {
        // The default response is usually EVA.Core.GetResponse
        // An extending class will know exactly what is returned and where
        // the data for <T> can be found
        //
        return this.data = _.map(response.Result.Page, function (page) {
            var order = new Order();
            order.setData(page);
            return order;
        });
    };
    /**
     * Fetches the list of items
     * @return {Promise<IEVAServiceResponse<EVA.Core.OrderDto, EVA.Core.Services.ListOrdersForCustomer, EVA.Core.Services.ListOrdersForCustomerResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    CustomerOrderList.prototype.fetch = function () {
        // Build the request data
        //
        var requestData = {
            ID: this.id,
            PageConfig: this.getPageConfiguration()
        };
        return _super.prototype.fetch.call(this, "ListOrdersForCustomer", requestData);
    };
    return CustomerOrderList;
}(base_1.FetchablePagedList));
exports.CustomerOrderList = CustomerOrderList;
/**
 * Provides access to a list of customer orders available in EVA
 */
var SearchOrderList = /** @class */ (function (_super) {
    __extends(SearchOrderList, _super);
    function SearchOrderList() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /**
         * The query string to search for in the products catalog
         * @type {string}
         */
        _this.query = "";
        /**
         * The list of order ids to fetch
         * @type {Array<number>}
         */
        _this.orderIds = [];
        return _this;
    }
    /**
     * Returns the current query string
     * @return {string}       The configured query string
     */
    SearchOrderList.prototype.getQuery = function () {
        return this.query;
    };
    /**
     * Set the organization id to search for
     * @param  {number} query The organization id
     * @return {number}       The configured organization id
     */
    SearchOrderList.prototype.setOrganizationID = function (id) {
        return this.organizationID = id;
    };
    /**
     * Returns the current organization id
     * @return {number}       The configured organization id
     */
    SearchOrderList.prototype.getOrganizationID = function () {
        return this.organizationID;
    };
    /**
     * Set the query to search for
     * @param  {string} query The new query string
     * @return {string}       The configured query string
     */
    SearchOrderList.prototype.setQuery = function (query) {
        return this.query = query;
    };
    /**
     * Returns the current list of order ids
     * @return {Array<number>} The configured list of order ids
     */
    SearchOrderList.prototype.getOrderIDs = function () {
        return this.orderIds;
    };
    /**
     * Adds an order ID to the list of orders to retrieve
     * @return {Array<number>} The configured list of order ids
     */
    SearchOrderList.prototype.addOrderID = function (id) {
        if (!_.isArray(this.orderIds)) {
            this.orderIds = [];
        }
        this.orderIds.push(id);
        return this.orderIds;
    };
    /**
     * Adds an order ID to the list of orders to retrieve
     * @return {Array<number>} The configured list of order ids
     */
    SearchOrderList.prototype.setOrderIDs = function (ids) {
        if (_.isArray(ids)) {
            this.orderIds = ids;
        }
        return this.orderIds;
    };
    /**
     * Clears the configured list of order ids
     * @return {Array<number>} The configured list of order ids
     */
    SearchOrderList.prototype.clearOrderIDs = function () {
        return this.orderIds = [];
    };
    /**
     * Fetches the list of items
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.SearchOrders, EVA.Core.Services.SearchOrdersResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    SearchOrderList.prototype.fetch = function () {
        // Build the request data
        //
        var requestData = {
            PageConfig: this.getPageConfiguration(),
        };
        // Amend request with optional parameters
        //
        if (this.query) {
            requestData.Query = this.query;
        }
        if (!_.isEmpty(this.orderIds)) {
            requestData.OrderIDs = this.orderIds;
        }
        if (this.organizationID) {
            requestData.OrganizationUnitID = this.organizationID;
        }
        return _super.prototype.fetch.call(this, "SearchOrders", requestData);
    };
    return SearchOrderList;
}(base_1.FetchablePagedList));
exports.SearchOrderList = SearchOrderList;
/**
 * Provides access to a list of customer orders available in EVA
 */
var OrdersWithReferenceList = /** @class */ (function (_super) {
    __extends(OrdersWithReferenceList, _super);
    function OrdersWithReferenceList() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Fetches the list of items
     * @return {Promise<IEVAServiceResponse<EVA.Core.OrderDto, EVA.Core.Services.ListOrdersWithReference, EVA.Core.Services.ListOrdersWithReferenceResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    OrdersWithReferenceList.prototype.fetch = function () {
        // Build the request data
        //
        var requestData = {
            PageConfig: this.getPageConfiguration()
        };
        return _super.prototype.fetch.call(this, "ListOrdersWithCustomerReferences", requestData);
    };
    return OrdersWithReferenceList;
}(base_1.FetchablePagedList));
exports.OrdersWithReferenceList = OrdersWithReferenceList;
/**
 * Provides access to a list of suspended orders available in EVA
 *
 * @export
 * @class SuspendedOrderList
 * @extends {FetchablePagedList<EVA.Core.Services.ListSuspendedOrdersResponseSuspendedOrderDto, EVA.Core.Services.ListSuspendedOrders, EVA.Core.Services.ListSuspendedOrdersResponse>}
 */
var SuspendedOrderList = /** @class */ (function (_super) {
    __extends(SuspendedOrderList, _super);
    function SuspendedOrderList() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Fetches the list of items
     */
    SuspendedOrderList.prototype.fetch = function () {
        // Build the request data
        //
        var requestData = {
            PageConfig: this.getPageConfiguration()
        };
        return _super.prototype.fetch.call(this, "ListSuspendedOrders", requestData);
    };
    return SuspendedOrderList;
}(base_1.FetchablePagedList));
exports.SuspendedOrderList = SuspendedOrderList;
